/**
 * Invitations: its own screen since Sep 17 2026 (Thomas).
 *
 * The join link, the code, the QR, the message to send, and how students
 * have arrived so far. Until Sep 17 2026 this was the dashboard's fifth
 * section, under four sections of figures, so an owner opening the
 * dashboard to invite students scrolled past the analytics to reach the
 * link. Now it is one click on the rail, on both hosts, and the dashboard
 * scroll is performance only. The day-one dashboard (the cohort-building
 * state) points here.
 *
 * What it renders is unchanged: the claim centre and the roster coverage
 * card (panels/InvitationsPanel.tsx), from the same view the dashboard
 * reads, so the two screens cannot disagree about a figure.
 */

import { useMemo } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import { SectionHead } from "~/app/components/primitives";
import { ADMIN_SECTIONS, INVITATIONS_PAGE } from "~/app/lib/admin-nav";
import { DEFAULT_FILTERS, deriveCohort, scenarioFromSearch } from "~/app/lib/demo-data";
import { liveView } from "~/app/lib/live-view";
import { viewerOf, type SessionPayload } from "~/app/lib/session";
import { InvitationsPanel } from "~/app/panels/InvitationsPanel";

export function AdminInvitations({
  basePath,
  demo = false,
  session,
}: {
  basePath: "/admin" | "/demo";
  demo?: boolean;
  /** The signed-in person, on /admin. Their institution's link and code replace the sample's. */
  session?: SessionPayload;
}) {
  const live = useLiveTenant(session);
  const scenario = useMemo(
    () => scenarioFromSearch(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
  const sampleView = useMemo(() => deriveCohort(scenario, DEFAULT_FILTERS), [scenario]);
  const data = live.state.kind === "ready" ? live.state.data : null;
  const view = useMemo(() => (data ? liveView(data) : sampleView), [data, sampleView]);
  const isLive = data !== null && session !== undefined;

  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }

  const { institution, seat } = view;
  const commands: PaletteCommand[] = [
    /* No join commands while the code is not issued (Sep 14 2026): a tenant a
       card checkout created has none until P3 connects it. */
    ...(institution.joinCode === ""
      ? []
      : [
          {
            id: "copy-link",
            group: "Claiming",
            label: "Copy the join link",
            hint: institution.joinUrl,
            keywords: "share students invite url",
            run: () => {
              void navigator.clipboard?.writeText(`https://${institution.joinUrl}`).catch(() => undefined);
            },
          },
          {
            id: "copy-code",
            group: "Claiming",
            label: "Copy the join code",
            hint: institution.joinCode,
            keywords: "share students invite code",
            run: () => {
              void navigator.clipboard?.writeText(institution.joinCode).catch(() => undefined);
            },
          },
        ]),
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
  ];

  return (
    <AdminShell
      institution={institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="invitations"
      isSample={!isLive}
      sections={[...ADMIN_SECTIONS]}
      seat={seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <section className="e-sec" style={{ marginTop: 0 }}>
        <SectionHead
          level="page"
          id="invitations"
          title={INVITATIONS_PAGE.title}
          lede={INVITATIONS_PAGE.lede}
        />


        <InvitationsPanel view={view} />
      </section>
    </AdminShell>
  );
}
